"use client"

import { useState, useEffect, useCallback } from "react"
import { Header } from "@/templates/Admin/components/Manager/header"
import { BorrowCart } from "@/templates/Admin/components/Manager/BorrowCart"
import type { CartItem } from "@/types"
import { useIdPath } from "@/contexts/IdPathProvider"
import { cartService } from "@/services/cart.service"
import { useSession } from 'next-auth/react'

interface CartPageProps {
  isOpen?: boolean
  onClose?: () => void
  isSidebarOpen?: boolean
  assetToAdd?: { uuid: string; title: string; type: string; thumbnail?: string } | null
  autoSelectAssetId?: string
  autoSubmitOnLoad?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function CartPage({ isOpen = true, onClose, assetToAdd, autoSelectAssetId, autoSubmitOnLoad }: CartPageProps) {
  const [cartItems, setCartItems] = useState<CartItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { idPath } = useIdPath();
  const { data: session } = useSession();

  // Get user-specific localStorage key
  const getCartStorageKey = useCallback(() => {
    const userId = (session as any)?.userData?.id || (session as any)?.userData?.user_id || (session?.user as any)?.id || (session?.user as any)?.user_id;
    return userId ? `borrowCart_${userId}` : 'borrowCart';
  }, [session]);

  const mapApiCartItems = (items: any[]): CartItem[] => {
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => ({
      id: item.asset_id,
      cartId: item.uuid || item.id || item.asset_id,
      asset_id: item.asset_id,
      title: item.name || `Asset ${item.asset_id?.slice(-6) || ''}`,
      type: item.type || '',
      coverImage: item.thumbnail,
      requestedQuantity: item.quantity,
      borrowDate: item.borrow_date,
      returnDate: item.return_date,
      available: true,
      waitlist: 0,
      authors: [],
      year: new Date().getFullYear(),
      description: '',
      subjects: [],
      location: 'Trong kho',
      language: 'Tiếng Việt',
    }));
  };

  const refreshCart = async (): Promise<CartItem[]> => {
    if (!idPath) return [];
    try {
      const items = await cartService.getCartItems(idPath);
      const transformedItems = mapApiCartItems(items);
      setCartItems(transformedItems);
      try {
        localStorage.setItem(getCartStorageKey(), JSON.stringify(transformedItems));
      } catch (storageErr) {
        console.warn("Failed to save cart to localStorage:", storageErr);
      }
      return transformedItems;
    } catch (e) {
      console.error("Failed to refresh cart:", e);
      return [];
    }
  };

  useEffect(() => {
    const loadCart = async () => {
      setIsLoading(true);
      if (!idPath) {
        try {
          const savedCart = localStorage.getItem(getCartStorageKey());
          if (savedCart) {
            const parsed = JSON.parse(savedCart);
            setCartItems(Array.isArray(parsed) ? parsed : []);
          }
        } catch (e) {
          console.warn("Failed to parse saved cart:", e);
        }
        setIsLoading(false);
        return;
      }
      try {
        await refreshCart();
      } catch (e) {
        console.error("Failed to load cart from API:", e);
        try {
          const savedCart = localStorage.getItem(getCartStorageKey());
          if (savedCart) {
            const parsed = JSON.parse(savedCart);
            setCartItems(Array.isArray(parsed) ? parsed : []);
          }
        } catch (storageErr) {
          console.warn("Failed to parse saved cart:", storageErr);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadCart();
  }, [idPath]);

  // Auto-add asset when assetToAdd prop is set
  useEffect(() => {
    if (!assetToAdd || !idPath) return

    let isMounted = true
    const targetUuid = assetToAdd.uuid

    const doAdd = async () => {
      try {
        await cartService.addToCart(idPath, {
          asset_id: targetUuid,
          quantity: 1,
          request_purpose: 'cho-muon',
        })
        if (isMounted) {
          await refreshCart()
        }
      } catch (err) {
        console.error('Failed to auto-add asset to cart:', err)
      }
    }

    doAdd()
    return () => { isMounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetToAdd, idPath])

  const updateCart = (newCartItems: CartItem[]) => {
    setCartItems(newCartItems)
    localStorage.setItem(getCartStorageKey(), JSON.stringify(newCartItems))
    window.dispatchEvent(new Event("cartUpdated"))
  }

  const handleRemoveCartItem = async (item: CartItem) => {
    if (!idPath || !item.cartId) {
      const fallbackItems = cartItems.filter((cartItem) => cartItem.id !== item.id)
      updateCart(fallbackItems)
      return fallbackItems
    }
    await cartService.removeFromCart(idPath, item.cartId)
    return await refreshCart()
  }

  const handleUpdateCartItem = async (
    item: CartItem,
    updates: { requestedQuantity?: number; borrowDate?: string; returnDate?: string },
  ) => {
    if (!idPath || !item.cartId) return
    const payload: any = {}
    if (updates.requestedQuantity !== undefined) payload.quantity = updates.requestedQuantity
    if (updates.borrowDate !== undefined) payload.borrow_date = updates.borrowDate
    if (updates.returnDate !== undefined) payload.return_date = updates.returnDate
    await cartService.updateCartItem(idPath, item.cartId, payload)
    return await refreshCart()
  }

  const handleRemoveSelectedItems = async (items: CartItem[]) => {
    if (!idPath) return
    const cartIds = items.map((item) => item.cartId).filter(Boolean) as string[]
    if (cartIds.length === 0) {
      const fallbackItems = cartItems.filter((item) => !items.find((removed) => removed.id === item.id))
      updateCart(fallbackItems)
      return fallbackItems
    }
    await Promise.all(cartIds.map((cartId) => cartService.removeFromCart(idPath, cartId)))
    return await refreshCart()
  }
  return (
    <main className="h-full min-h-screen bg-gray-50 overflow-y-auto">
      <Header />
      <div className="container mx-auto px-4 py-6">
        <BorrowCart
          cartItems={cartItems}
          onUpdateCart={updateCart}
          onRemoveItem={handleRemoveCartItem}
          onUpdateItem={handleUpdateCartItem}
          onRemoveSelectedItems={handleRemoveSelectedItems}
          onBackToLibrary={onClose}
          isLoading={isLoading}
          autoSelectAssetId={autoSelectAssetId}
          autoSubmitOnLoad={autoSubmitOnLoad}
        />
      </div>
    </main>
  )
}